import { z } from 'zod';
import { ActivationId, GateId } from './activation.ts';
import { Id, IsoDateTime, JsonObject, Sha256Hex, TenantId } from './common.ts';
import { Deadline } from './deadline.ts';
import { ResourceScope } from './entitlement.ts';
import { PlatformError } from './errors.ts';
import { Principal } from './principal.ts';

export const ApprovalStatus = z
  .enum(['pending', 'approved', 'rejected', 'expired', 'invalidated'])
  .meta({ id: 'ApprovalStatus' });
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;

/** Policy facts captured when the approval was requested. */
export const PolicyContext = z
  .looseObject({
    policyVersion: z.string(),
    bundles: z.array(z.string()).default([]),
    obligations: z.array(z.string()).default([]),
    reasons: z.array(z.string()).default([]),
  })
  .meta({ id: 'PolicyContext' });
export type PolicyContext = z.infer<typeof PolicyContext>;

/**
 * An action-bound approval. Valid for execution only while `status = approved`, not expired,
 * `actionHashes[actionId] === intent.argsHash` and `targetVersions[actionId]` equals the current target version.
 */
export const Approval = z
  .object({
    approvalId: Id,
    taskId: Id,
    tenantId: TenantId,
    actionIds: z.array(Id).min(1),
    proposalHash: Sha256Hex,
    /** actionId → argsHash at request time. */
    actionHashes: z.record(z.string(), Sha256Hex),
    /** actionId → target version at request time (empty string when the target does not exist yet). */
    targetVersions: z.record(z.string(), z.string()),
    /**
     * Added by 30 #11: every actionId of the proposal this approval binds (`proposalHash`), the approval-bound
     * `actionIds` and their unbound siblings. Only writes of these actions may explain target version drift (own-write
     * drift). Absent on approvals requested before 30: every own write of the task explains drift, as before 30.
     */
    proposalActionIds: z.array(Id).optional(),
    requestedAt: IsoDateTime,
    expiresAt: IsoDateTime,
    approver: Principal.optional(),
    decision: z.enum(['approved', 'rejected']).optional(),
    decidedAt: IsoDateTime.optional(),
    rationale: z.string().optional(),
    evidenceIds: z.array(z.string()).default([]),
    policyContext: PolicyContext,
    status: ApprovalStatus,
  })
  .meta({ id: 'Approval' });
export type Approval = z.infer<typeof Approval>;

// =================================================================================================
// 17a (ADR-016): approval requests with revisions, typed subjects, stages and separation of duties.
// Additive: `Approval` above (one action-bound record) is unchanged and keeps serving the governed task.
// =================================================================================================

/** A decision on one stage. `reject` is terminal for the request; `return` asks for a new revision. */
export const ApprovalDecisionValue = z
  .enum(['approve', 'reject', 'return'])
  .meta({ id: 'ApprovalDecisionValue' });
export type ApprovalDecisionValue = z.infer<typeof ApprovalDecisionValue>;

/** Subject bound to an action set (today's binding): exactly these actions with these hashes and versions. */
export const ActionSetSubject = z
  .object({
    kind: z.literal('actions'),
    taskId: Id,
    actionIds: z.array(Id).min(1),
    proposalHash: Sha256Hex,
    actionHashes: z.record(z.string(), Sha256Hex),
    targetVersions: z.record(z.string(), z.string()),
  })
  .meta({ id: 'ActionSetSubject' });
export type ActionSetSubject = z.infer<typeof ActionSetSubject>;

/**
 * Subject bound to a business document revision (e.g. a purchase requisition), approved before any action
 * exists. Later actions carry it as a `SubjectPrecondition` that the tool gateway verifies.
 */
export const BusinessSubject = z
  .object({
    kind: z.literal('business'),
    type: z.string().min(1).max(200),
    id: z.string().min(1).max(200),
    revision: z.string().min(1).max(100),
    /** `hashCanonical` of the subject content the approvers saw. */
    hash: Sha256Hex,
  })
  .meta({ id: 'BusinessSubject' });
export type BusinessSubject = z.infer<typeof BusinessSubject>;

export const ApprovalSubject = z
  .discriminatedUnion('kind', [ActionSetSubject, BusinessSubject])
  .meta({ id: 'ApprovalSubject' });
export type ApprovalSubject = z.infer<typeof ApprovalSubject>;

/**
 * Declared on an `ActionIntent`: the action may execute only while an approval request for exactly this business
 * subject (type, id, revision, hash) is `approved`.
 */
export const SubjectPrecondition = z
  .object({
    type: z.string().min(1).max(200),
    id: z.string().min(1).max(200),
    revision: z.string().min(1).max(100),
    hash: Sha256Hex,
    /** Gate id the approval must come from, when the process declares several gates for one subject type. */
    gateId: GateId.optional(),
  })
  .meta({ id: 'SubjectPrecondition' });
export type SubjectPrecondition = z.infer<typeof SubjectPrecondition>;

/** One stage of a route. Stages with equal `order` run in parallel; lower orders complete first. */
export const ApprovalStage = z
  .object({
    stageId: z.string().regex(/^[a-z][a-z0-9_.-]{0,62}$/, 'stage id must be lowercase'),
    title: z.string().min(1).max(200).optional(),
    /** Role a decider must hold (pack-declared or platform). */
    requiredRole: z.string().min(1),
    /** The decider needs `decide` entitlement on this scope (defaults to the request scope). */
    requiredScope: ResourceScope.optional(),
    /** Distinct approving identities needed. */
    quorum: z.number().int().positive().default(1),
    order: z.number().int().nonnegative(),
    /** Stage-level expiry: when it passes while the stage is open and pending, the request expires. */
    expiresAt: IsoDateTime.optional(),
    /**
     * `priorDeciders`: nobody in `sod.priorDeciderIds` (earlier revisions and lineages) may decide this stage,
     * e.g. a final sign-off that must be independent of everyone who reviewed before.
     */
    distinctFrom: z.enum(['priorDeciders']).optional(),
  })
  .meta({ id: 'ApprovalStage' });
export type ApprovalStage = z.infer<typeof ApprovalStage>;
export type ApprovalStageInput = z.input<typeof ApprovalStage>;

/** Route resolved by the pack from typed policy facts at request time. */
export const ApprovalRoute = z
  .object({
    routeKey: z.string().min(1),
    policyVersion: z.string().min(1),
    stages: z.array(ApprovalStage).min(1),
  })
  .meta({ id: 'ApprovalRoute' });
export type ApprovalRoute = z.infer<typeof ApprovalRoute>;

/** One individual decision, bound to the authenticated identity (not to a role) and to one request revision. */
export const ApprovalStageDecision = z
  .object({
    decisionId: Id,
    requestId: Id,
    revision: z.number().int().positive(),
    stageId: z.string().min(1),
    principalId: z.string().min(1),
    /** Role the decider used (audit); SoD is evaluated on `principalId`. */
    role: z.string().min(1).optional(),
    decision: ApprovalDecisionValue,
    rationale: z.string().min(1).max(4000),
    at: IsoDateTime,
    /** Set when the vote stopped counting (decider's entitlement revoked while the request was pending). */
    invalidated: z.object({ at: IsoDateTime, reason: z.string().min(1) }).optional(),
  })
  .meta({ id: 'ApprovalStageDecision' });
export type ApprovalStageDecision = z.infer<typeof ApprovalStageDecision>;

/**
 * Separation-of-duties facts. They persist across tasks, revisions and lineages: a revision or a new lineage
 * inherits them and adds the previous deciders.
 */
export const SodFacts = z
  .object({
    requesterIds: z.array(z.string().min(1)).default([]),
    preparerIds: z.array(z.string().min(1)).default([]),
    /** Everyone who decided an earlier revision or lineage of this subject. */
    priorDeciderIds: z.array(z.string().min(1)).default([]),
    /** Stage of each earlier decision: a person who decided stage A may not later decide stage B. */
    priorDecisions: z
      .array(
        z.object({ principalId: z.string().min(1), stageId: z.string().min(1), revision: z.number().int() }),
      )
      .default([]),
    /**
     * Identities barred from deciding this request because of a duty elsewhere (SoD across gates), e.g. the
     * approver of the purchase requisition is excluded from the payment authorisation.
     */
    excludedDeciderIds: z
      .array(
        z.object({
          principalId: z.string().min(1),
          reason: z.string().min(1),
          sourceRequestId: Id,
        }),
      )
      .default([]),
  })
  .meta({ id: 'SodFacts' });
export type SodFacts = z.infer<typeof SodFacts>;

export const ApprovalRequestStatus = z
  .enum(['pending', 'approved', 'rejected', 'returned', 'expired', 'invalidated', 'cancelled'])
  .meta({ id: 'ApprovalRequestStatus' });
export type ApprovalRequestStatus = z.infer<typeof ApprovalRequestStatus>;

export const ApprovalEscalation = z
  .object({
    afterSeconds: z.number().int().positive(),
    toRole: z.string().min(1),
    /**
     * Notify only, or add the role as an alternative decider of the open stages (SoD rules still apply).
     * Added by 21 step 0 (brief 21 §2, ADR-021): `reassign` — the open stages' required role becomes `toRole`
     * (or `principalId` is the only eligible decider; SoD still applies); `raise_gate` — open a further gate
     * (escalation review) for the subject. `notify` sends through the `Notifier` port (`templateKey`).
     */
    mode: z.enum(['notify', 'add_decider', 'reassign', 'raise_gate']).default('notify'),
    /** Added by 21 step 0: `reassign` to one principal. */
    principalId: z.string().min(1).optional(),
    /** Added by 21 step 0: notification template (default `platform.approval.escalated`). */
    templateKey: z.string().min(1).max(200).optional(),
    /** Added by 21 step 0: `raise_gate`: the gate to open. */
    gateId: GateId.optional(),
  })
  .meta({ id: 'ApprovalEscalation' });
export type ApprovalEscalation = z.infer<typeof ApprovalEscalation>;

/**
 * An immutable approval request revision. Decisions accumulate only on `pending` revisions; `return` closes the
 * revision as `returned` and the next revision (`reviseApprovalRequest`) starts with no votes. A changed
 * subject opens a new lineage (`openNewLineage`) that lists earlier lineages in `priorLineageIds`.
 */
export const ApprovalRequest = z
  .object({
    requestId: Id,
    tenantId: TenantId,
    revision: z.number().int().positive(),
    previousRevisionId: Id.optional(),
    /** Last request ids of earlier lineages for the same gate and subject id (SoD history follows them). */
    priorLineageIds: z.array(Id).default([]),
    caseId: Id.optional(),
    processInstanceId: Id.optional(),
    activationId: ActivationId.optional(),
    gateId: GateId.optional(),
    subject: ApprovalSubject,
    route: ApprovalRoute,
    /** Typed policy facts the pack supplied to resolve the route (amount/currency are P2P facts, not core). */
    policyFacts: JsonObject.default({}),
    scope: ResourceScope.default({}),
    decisions: z.array(ApprovalStageDecision).default([]),
    sod: SodFacts.prefault({}),
    status: ApprovalRequestStatus,
    requestedBy: z.string().min(1),
    createdAt: IsoDateTime,
    expiresAt: IsoDateTime,
    escalation: ApprovalEscalation.optional(),
    closedAt: IsoDateTime.optional(),
    /** Added by 21 step 0: business deadline of the decision (expiry keeps `expiresAt` and `approvals.expireDue`). */
    deadline: Deadline.optional(),
  })
  .meta({ id: 'ApprovalRequest' });
export type ApprovalRequest = z.infer<typeof ApprovalRequest>;
export type ApprovalRequestInput = z.input<typeof ApprovalRequest>;

/** Signal delivered to the run when an approval request revision closes: `approval:<requestId>`. */
export function approvalSignalName(requestId: string): string {
  return `approval:${requestId}`;
}

// Pure rules --------------------------------------------------------------------------------------

/** Stages grouped by `order`, ascending. Throws `validation_failed` on duplicate stage ids. */
export function stageGroups(stages: readonly ApprovalStage[]): ApprovalStage[][] {
  const ids = stages.map((s) => s.stageId);
  const dup = ids.find((id, i) => ids.indexOf(id) !== i);
  if (dup !== undefined) {
    throw new PlatformError('validation_failed', `Duplicate approval stage id "${dup}"`, {
      details: { stageId: dup },
    });
  }
  const orders = [...new Set(stages.map((s) => s.order))].sort((a, b) => a - b);
  return orders.map((o) => stages.filter((s) => s.order === o));
}

/**
 * Decisions that count for this revision: same request id and revision, not invalidated. A vote for another
 * request id or revision, or of a revoked decider, never counts.
 */
export function validVotes(
  request: Pick<ApprovalRequest, 'requestId' | 'revision' | 'decisions'>,
): ApprovalStageDecision[] {
  return request.decisions.filter(
    (d) =>
      d.requestId === request.requestId && d.revision === request.revision && d.invalidated === undefined,
  );
}

export type StageOutcome = 'pending' | 'approved' | 'rejected' | 'returned';

/**
 * Outcome of one stage from its valid votes. Each principal counts once (first decision wins). Precedence:
 * reject > return > approve-quorum > pending.
 */
export function stageOutcome(
  stage: ApprovalStage,
  decisions: readonly ApprovalStageDecision[],
): StageOutcome {
  const seen = new Set<string>();
  const firsts: ApprovalStageDecision[] = [];
  for (const d of decisions) {
    if (d.stageId !== stage.stageId || seen.has(d.principalId)) continue;
    seen.add(d.principalId);
    firsts.push(d);
  }
  if (firsts.some((d) => d.decision === 'reject')) return 'rejected';
  if (firsts.some((d) => d.decision === 'return')) return 'returned';
  return firsts.filter((d) => d.decision === 'approve').length >= stage.quorum ? 'approved' : 'pending';
}

/** Pending stages of the lowest order group that is not fully approved (ignores clock and status). */
function pendingGroup(request: ApprovalRequest, votes: readonly ApprovalStageDecision[]): ApprovalStage[] {
  for (const group of stageGroups(request.route.stages)) {
    const pending = group.filter((s) => stageOutcome(s, votes) === 'pending');
    if (pending.length > 0) return pending;
  }
  return [];
}

/**
 * Status computed from the request's valid votes and the clock. Terminal statuses (`approved`, `rejected`,
 * `returned`, `invalidated`, `cancelled`, `expired`) are kept as stored. A pending request expires at its
 * `expiresAt`, or when a pending stage of the open group passes its own `expiresAt`.
 */
export function evaluateApprovalRequest(request: ApprovalRequest, now: string): ApprovalRequestStatus {
  if (request.status !== 'pending') return request.status;
  const votes = validVotes(request);
  const outcomes = request.route.stages.map((s) => stageOutcome(s, votes));
  if (outcomes.includes('rejected')) return 'rejected';
  if (outcomes.includes('returned')) return 'returned';
  if (outcomes.every((o) => o === 'approved')) return 'approved';
  const t = Date.parse(now);
  if (t >= Date.parse(request.expiresAt)) return 'expired';
  const stageExpired = pendingGroup(request, votes).some(
    (s) => s.expiresAt !== undefined && t >= Date.parse(s.expiresAt),
  );
  return stageExpired ? 'expired' : 'pending';
}

/** Stages a decider may act on now: pending stages of the lowest order group that is not fully approved. */
export function openStages(request: ApprovalRequest, now: string): ApprovalStage[] {
  if (evaluateApprovalRequest(request, now) !== 'pending') return [];
  return pendingGroup(request, validVotes(request));
}

/** True when the request's `add_decider` escalation is in force at `now`. */
export function escalationActive(request: ApprovalRequest, now: string): boolean {
  const e = request.escalation;
  return (
    e !== undefined &&
    e.mode === 'add_decider' &&
    Date.parse(now) >= Date.parse(request.createdAt) + e.afterSeconds * 1000
  );
}

/**
 * Added by 21b (brief 21 §2, esc-05): true once any escalation mode is in force (`afterSeconds` after creation).
 * `escalationActive` keeps its meaning (an `add_decider` escalation in force).
 */
export function escalationInForce(request: ApprovalRequest, now: string): boolean {
  const e = request.escalation;
  return e !== undefined && Date.parse(now) >= Date.parse(request.createdAt) + e.afterSeconds * 1000;
}

/**
 * Added by 21b: true when a `reassign` escalation is in force: the open stages' eligible decider becomes
 * `principalId` (when set) or a holder of `toRole` instead of the stage's `requiredRole`; SoD still applies.
 */
export function reassignActive(request: ApprovalRequest, now: string): boolean {
  return request.escalation?.mode === 'reassign' && escalationInForce(request, now);
}

export type DecisionRefusal =
  | 'not_pending'
  | 'stale_revision'
  | 'unknown_stage'
  | 'stage_not_open'
  | 'role_missing'
  | 'sod_requester'
  | 'sod_preparer'
  | 'sod_excluded'
  | 'sod_prior_decider'
  | 'sod_other_stage'
  | 'duplicate_vote';

/**
 * Whether `principalId` (holding `roles`) may record a decision on `stageId` of this revision. Rules (ADR-016):
 * - the stage must be open; the decider holds `requiredRole`, or the escalation role while an `add_decider`
 *   escalation is in force;
 * - requesters, preparers and `sod.excludedDeciderIds` never decide;
 * - a stage with `distinctFrom: 'priorDeciders'` refuses everyone in `sod.priorDeciderIds`;
 * - one identity decides at most one stage across revisions and lineages (current votes and
 *   `sod.priorDecisions`), whatever roles it holds; one vote per identity per stage.
 * Scope entitlement (`approval.decide.scoped` on `requiredScope`) is checked separately by the caller.
 */
export function checkDecisionAllowed(
  request: ApprovalRequest,
  input: {
    principalId: string;
    roles: readonly string[];
    stageId: string;
    requestId?: string;
    revision?: number;
  },
  now: string,
): { allowed: true } | { allowed: false; reason: DecisionRefusal } {
  const who = input.principalId;
  if (
    (input.requestId !== undefined && input.requestId !== request.requestId) ||
    (input.revision !== undefined && input.revision !== request.revision)
  ) {
    return { allowed: false, reason: 'stale_revision' };
  }
  if (evaluateApprovalRequest(request, now) !== 'pending') return { allowed: false, reason: 'not_pending' };
  const stage = request.route.stages.find((s) => s.stageId === input.stageId);
  if (stage === undefined) return { allowed: false, reason: 'unknown_stage' };
  if (!openStages(request, now).some((s) => s.stageId === input.stageId)) {
    return { allowed: false, reason: 'stage_not_open' };
  }
  const escalationRole =
    escalationActive(request, now) && request.escalation !== undefined
      ? request.escalation.toRole
      : undefined;
  // 21b: a `reassign` escalation in force replaces the stage role (principal, else `toRole`); SoD rules below apply.
  const reassigned = reassignActive(request, now) ? request.escalation : undefined;
  const hasRole = reassigned
    ? reassigned.principalId !== undefined
      ? reassigned.principalId === who
      : input.roles.includes(reassigned.toRole)
    : input.roles.includes(stage.requiredRole) ||
      (escalationRole !== undefined && input.roles.includes(escalationRole));
  if (!hasRole) return { allowed: false, reason: 'role_missing' };
  if (request.sod.requesterIds.includes(who)) return { allowed: false, reason: 'sod_requester' };
  if (request.sod.preparerIds.includes(who)) return { allowed: false, reason: 'sod_preparer' };
  if (request.sod.excludedDeciderIds.some((e) => e.principalId === who)) {
    return { allowed: false, reason: 'sod_excluded' };
  }
  if (stage.distinctFrom === 'priorDeciders' && request.sod.priorDeciderIds.includes(who)) {
    return { allowed: false, reason: 'sod_prior_decider' };
  }
  const votes = validVotes(request).filter((d) => d.principalId === who);
  if (votes.some((d) => d.stageId === input.stageId)) return { allowed: false, reason: 'duplicate_vote' };
  const otherStage =
    votes.some((d) => d.stageId !== input.stageId) ||
    request.sod.priorDecisions.some((d) => d.principalId === who && d.stageId !== input.stageId);
  if (otherStage) return { allowed: false, reason: 'sod_other_stage' };
  return { allowed: true };
}

/**
 * Appends a decision and recomputes the status. Throws `approval_invalid` (details.reason = the refusal) when
 * `checkDecisionAllowed` refuses. Concurrent votes are serialised by the store; this function is the rule each
 * serialised write applies.
 */
export function applyDecision(
  request: ApprovalRequest,
  decision: ApprovalStageDecision,
  roles: readonly string[],
): ApprovalRequest {
  const check = checkDecisionAllowed(
    request,
    {
      principalId: decision.principalId,
      roles,
      stageId: decision.stageId,
      requestId: decision.requestId,
      revision: decision.revision,
    },
    decision.at,
  );
  if (!check.allowed) {
    throw new PlatformError('approval_invalid', `Decision refused: ${check.reason}`, {
      details: { reason: check.reason, requestId: request.requestId, stageId: decision.stageId },
    });
  }
  const next: ApprovalRequest = { ...request, decisions: [...request.decisions, decision] };
  const status = evaluateApprovalRequest(next, decision.at);
  return { ...next, status, ...(status === 'pending' ? {} : { closedAt: decision.at }) };
}

/**
 * Revocation rule (ADR-016 §10): when a decider's entitlement is revoked, their recorded votes on a request that
 * is still **pending** are marked invalidated (kept for audit, no longer counted). Closed requests are unchanged.
 */
export function invalidateVotesOf(
  request: ApprovalRequest,
  principalId: string,
  reason: string,
  at: string,
): ApprovalRequest {
  if (request.status !== 'pending') return request;
  let changed = false;
  const decisions = request.decisions.map((d) => {
    if (d.principalId !== principalId || d.invalidated !== undefined) return d;
    changed = true;
    return { ...d, invalidated: { at, reason } };
  });
  return changed ? { ...request, decisions } : request;
}

export interface ApprovalRevisionInput {
  requestId: string;
  subject: ApprovalSubject;
  createdAt: string;
  expiresAt: string;
  /** New route when the policy facts changed (e.g. the amount moved to another band). */
  route?: ApprovalRoute;
  policyFacts?: Record<string, unknown>;
  /** Preparer of the new revision (added to SoD facts). */
  preparerId?: string;
}

/** SoD facts of `previous` plus its counted votes (shared by revisions and new lineages). */
function inheritSod(previous: ApprovalRequest, preparerId: string | undefined): SodFacts {
  const votes = validVotes(previous);
  const deciders = [...new Set([...previous.sod.priorDeciderIds, ...votes.map((d) => d.principalId)])];
  const preparers =
    preparerId === undefined || previous.sod.preparerIds.includes(preparerId)
      ? previous.sod.preparerIds
      : [...previous.sod.preparerIds, preparerId];
  return {
    requesterIds: previous.sod.requesterIds,
    preparerIds: preparers,
    priorDeciderIds: deciders,
    priorDecisions: [
      ...previous.sod.priorDecisions,
      ...votes.map((d) => ({ principalId: d.principalId, stageId: d.stageId, revision: d.revision })),
    ],
    excludedDeciderIds: previous.sod.excludedDeciderIds,
  };
}

/**
 * The revision rule (ADR-016): only a `returned` (or `invalidated`) revision can be revised; `rejected` is
 * terminal. The new revision has `revision + 1`, links `previousRevisionId`, starts with **no votes** (every vote
 * of the old revision is invalid for it), and inherits SoD facts plus the old revision's deciders.
 */
export function reviseApprovalRequest(
  previous: ApprovalRequest,
  input: ApprovalRevisionInput,
): ApprovalRequest {
  if (previous.status !== 'returned' && previous.status !== 'invalidated') {
    throw new PlatformError(
      'approval_invalid',
      `Cannot revise an approval request in status ${previous.status}`,
      {
        details: { reason: 'not_revisable', status: previous.status, requestId: previous.requestId },
      },
    );
  }
  const { closedAt: _closedAt, ...open } = previous;
  return {
    ...open,
    requestId: input.requestId,
    revision: previous.revision + 1,
    previousRevisionId: previous.requestId,
    subject: input.subject,
    route: input.route ?? previous.route,
    policyFacts: input.policyFacts ?? previous.policyFacts,
    decisions: [],
    sod: inheritSod(previous, input.preparerId),
    status: 'pending',
    createdAt: input.createdAt,
    expiresAt: input.expiresAt,
  };
}

/**
 * Identity of what a gate decides: `<gateId>|business|<type>|<id>|<revision>` or
 * `<gateId>|actions|<taskId>|<proposalHash>`. A reject is terminal for this key.
 */
export function approvalSubjectKey(gateId: string | undefined, subject: ApprovalSubject): string {
  const gate = gateId ?? '-';
  return subject.kind === 'business'
    ? `${gate}|business|${subject.type}|${subject.id}|${subject.revision}`
    : `${gate}|actions|${subject.taskId}|${subject.proposalHash}`;
}

/**
 * Reject is terminal per `(gateId, subject.type, subject.id, subject.revision)` (ADR-016 §5): a new request for
 * a subject key that any earlier request rejected throws `approval_subject_rejected`. `existing` = requests of the
 * same tenant for that gate and subject id.
 */
export function assertSubjectNotRejected(
  candidate: { gateId?: string; subject: ApprovalSubject },
  existing: readonly Pick<ApprovalRequest, 'requestId' | 'gateId' | 'subject' | 'status'>[],
): void {
  const key = approvalSubjectKey(candidate.gateId, candidate.subject);
  const rejected = existing.find(
    (r) => r.status === 'rejected' && approvalSubjectKey(r.gateId, r.subject) === key,
  );
  if (rejected !== undefined) {
    throw new PlatformError('approval_subject_rejected', 'This subject revision was rejected at this gate', {
      details: { subjectKey: key, rejectedRequestId: rejected.requestId },
    });
  }
}

/**
 * Opens a new lineage after the subject changed (new `subject.revision` or, for action subjects, a new
 * proposal): revision 1, no votes, `priorLineageIds` = earlier lineages + `previous.requestId`, SoD facts and
 * deciders inherited so SoD history follows the subject. `previous` must be closed (not `pending`) and the
 * subject key must differ (`approval_subject_rejected` when the unchanged subject was rejected,
 * `approval_invalid` otherwise).
 */
export function openNewLineage(previous: ApprovalRequest, input: ApprovalRevisionInput): ApprovalRequest {
  if (previous.status === 'pending') {
    throw new PlatformError('approval_invalid', 'Close the pending request before opening a new lineage', {
      details: { reason: 'previous_pending', requestId: previous.requestId },
    });
  }
  assertSubjectNotRejected(
    { ...(previous.gateId ? { gateId: previous.gateId } : {}), subject: input.subject },
    [previous],
  );
  if (
    approvalSubjectKey(previous.gateId, input.subject) ===
    approvalSubjectKey(previous.gateId, previous.subject)
  ) {
    throw new PlatformError('approval_invalid', 'A new lineage needs a changed subject revision', {
      details: { reason: 'subject_unchanged', requestId: previous.requestId },
    });
  }
  const { closedAt: _closedAt, previousRevisionId: _prev, ...rest } = previous;
  return {
    ...rest,
    requestId: input.requestId,
    revision: 1,
    priorLineageIds: [...previous.priorLineageIds, previous.requestId],
    subject: input.subject,
    route: input.route ?? previous.route,
    policyFacts: input.policyFacts ?? previous.policyFacts,
    decisions: [],
    sod: inheritSod(previous, input.preparerId),
    status: 'pending',
    createdAt: input.createdAt,
    expiresAt: input.expiresAt,
  };
}

/** True when `request` is an approved revision of exactly the business subject the precondition names. */
export function subjectSatisfies(precondition: SubjectPrecondition, request: ApprovalRequest): boolean {
  const s = request.subject;
  return (
    request.status === 'approved' &&
    s.kind === 'business' &&
    s.type === precondition.type &&
    s.id === precondition.id &&
    s.revision === precondition.revision &&
    s.hash === precondition.hash &&
    (precondition.gateId === undefined || request.gateId === precondition.gateId)
  );
}

// Route tables (17b2) ----------------------------------------------------------------------------------

/**
 * One condition on a typed policy fact (dotted path into `policyFacts`). Core never names a fact: the pack's route
 * table does (amount and currency are pack facts, not core fields).
 */
export const RouteFactCondition = z
  .object({
    fact: z.string().min(1).max(200),
    op: z.enum(['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'in', 'exists']),
    value: z.unknown().optional(),
  })
  .meta({ id: 'RouteFactCondition' });
export type RouteFactCondition = z.infer<typeof RouteFactCondition>;

/** A rule of a route table: when every condition holds, the request gets these stages. */
export const ApprovalRouteRule = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9_.-]{0,62}$/, 'rule id must be lowercase'),
    when: z.array(RouteFactCondition).default([]),
    stages: z.array(ApprovalStage).min(1),
    /** Request lifetime for this rule (overrides the table default). */
    ttlSeconds: z.number().int().positive().optional(),
    escalation: ApprovalEscalation.optional(),
  })
  .meta({ id: 'ApprovalRouteRule' });
export type ApprovalRouteRule = z.infer<typeof ApprovalRouteRule>;
export type ApprovalRouteRuleInput = z.input<typeof ApprovalRouteRule>;

/**
 * Pack-supplied routing table for one `routeKey` (a `GateDefinition.routeKey`). Rules are evaluated in order; the
 * first whose conditions all hold wins. No match → `validation_failed` (`details.reason = 'no_route'`).
 */
export const ApprovalRouteTable = z
  .object({
    routeKey: z.string().min(1).max(200),
    policyVersion: z.string().min(1),
    rules: z.array(ApprovalRouteRule).min(1),
    /** Default request lifetime. */
    defaultTtlSeconds: z.number().int().positive().default(86_400),
  })
  .meta({ id: 'ApprovalRouteTable' });
export type ApprovalRouteTable = z.infer<typeof ApprovalRouteTable>;
export type ApprovalRouteTableInput = z.input<typeof ApprovalRouteTable>;

function factAt(facts: Record<string, unknown>, path: string): unknown {
  let value: unknown = facts;
  for (const part of path.split('.')) {
    if (value === null || typeof value !== 'object') return undefined;
    value = (value as Record<string, unknown>)[part];
  }
  return value;
}

/** True when `condition` holds for `facts`. Ordering ops compare numbers, or strings with strings. */
export function routeConditionHolds(condition: RouteFactCondition, facts: Record<string, unknown>): boolean {
  const actual = factAt(facts, condition.fact);
  const expected = condition.value;
  const comparable =
    (typeof actual === 'number' && typeof expected === 'number') ||
    (typeof actual === 'string' && typeof expected === 'string');
  switch (condition.op) {
    case 'exists':
      return actual !== undefined && actual !== null;
    case 'eq':
      return actual === expected;
    case 'neq':
      return actual !== expected;
    case 'in':
      return Array.isArray(expected) && expected.includes(actual);
    case 'lt':
      return comparable && (actual as number | string) < (expected as number | string);
    case 'lte':
      return comparable && (actual as number | string) <= (expected as number | string);
    case 'gt':
      return comparable && (actual as number | string) > (expected as number | string);
    case 'gte':
      return comparable && (actual as number | string) >= (expected as number | string);
  }
}

export interface ResolvedApprovalRoute {
  route: ApprovalRoute;
  ruleId: string;
  ttlSeconds: number;
  escalation?: ApprovalEscalation;
}

/**
 * Minimal route resolver (17b2): the first rule of `table` whose conditions all hold for `facts`. Throws
 * `validation_failed` (`details.reason = 'no_route'`) when none matches.
 */
export function resolveApprovalRoute(
  tableInput: ApprovalRouteTableInput,
  facts: Record<string, unknown>,
): ResolvedApprovalRoute {
  const table = ApprovalRouteTable.parse(tableInput);
  for (const rule of table.rules) {
    if (!rule.when.every((c) => routeConditionHolds(c, facts))) continue;
    stageGroups(rule.stages);
    return {
      route: { routeKey: table.routeKey, policyVersion: table.policyVersion, stages: rule.stages },
      ruleId: rule.id,
      ttlSeconds: rule.ttlSeconds ?? table.defaultTtlSeconds,
      ...(rule.escalation ? { escalation: rule.escalation } : {}),
    };
  }
  throw new PlatformError('validation_failed', `No approval route of ${table.routeKey} matches the facts`, {
    details: { reason: 'no_route', routeKey: table.routeKey },
  });
}

// Events ------------------------------------------------------------------------------------------

export const APPROVAL_REQUEST_EVENT_TYPES = {
  created: 'approval.request.created',
  decided: 'approval.request.decided',
  closed: 'approval.request.closed',
} as const;

/** `approval.request.created` (also for a new revision). */
export const ApprovalRequestCreatedData = z
  .object({
    requestId: Id,
    revision: z.number().int().positive(),
    previousRevisionId: Id.optional(),
    caseId: Id.optional(),
    processInstanceId: Id.optional(),
    activationId: ActivationId.optional(),
    gateId: GateId.optional(),
    subjectKind: z.enum(['actions', 'business']),
    routeKey: z.string(),
    stageIds: z.array(z.string()),
    expiresAt: IsoDateTime,
    at: IsoDateTime,
  })
  .meta({ id: 'ApprovalRequestCreatedData' });
export type ApprovalRequestCreatedData = z.infer<typeof ApprovalRequestCreatedData>;

/** `approval.request.decided`: one stage decision was recorded. */
export const ApprovalRequestDecidedData = z
  .object({
    requestId: Id,
    revision: z.number().int().positive(),
    stageId: z.string(),
    decision: ApprovalDecisionValue,
    principalId: z.string(),
    status: ApprovalRequestStatus,
    at: IsoDateTime,
  })
  .meta({ id: 'ApprovalRequestDecidedData' });
export type ApprovalRequestDecidedData = z.infer<typeof ApprovalRequestDecidedData>;

/** `approval.request.closed`: the revision reached a non-pending status (delivered to the run as a signal). */
export const ApprovalRequestClosedData = z
  .object({
    requestId: Id,
    revision: z.number().int().positive(),
    status: ApprovalRequestStatus,
    activationId: ActivationId.optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'ApprovalRequestClosedData' });
export type ApprovalRequestClosedData = z.infer<typeof ApprovalRequestClosedData>;
